import { nextMenuIndex } from '@/features/common/RowActions/menuNavigation';

// A tablist steps sideways; its keys are the menu's turned through a quarter.
const AS_VERTICAL: Record<string, string> = { ArrowRight: 'ArrowDown', ArrowLeft: 'ArrowUp' };

/**
 * The option — or tab — a key moves the focus to: the arrows of the list's own direction step and
 * wrap, Home and End jump to the ends. Null for any other key, which is left to the browser; the
 * arithmetic is the menu's (nextMenuIndex), so a list, a menu and a tablist step the same way.
 */
export function nextIndex(
  current: number,
  key: string,
  count: number,
  orientation: 'vertical' | 'horizontal' = 'vertical',
): number | null {
  if (orientation === 'horizontal') {
    if (key === 'ArrowDown' || key === 'ArrowUp') return null;
    return nextMenuIndex(current, AS_VERTICAL[key] ?? key, count);
  }
  return nextMenuIndex(current, key, count);
}

/**
 * The option a typed prefix lands on (WAI-ARIA listbox type-ahead): the first label, searching on
 * from `from`, that starts with what was typed in the last moment, ignoring case.
 *
 * The same letter typed again — "a", "aa", "aaa" — steps through the labels starting with it
 * instead of looking for a label that starts "aa", so one key cycles through its section of a long
 * list. A longer prefix is matched from the current option itself: typing on only narrows, it does
 * not skip a label that still fits. Null when nothing starts that way.
 */
export function typeAheadIndex(labels: string[], buffer: string, from: number): number | null {
  if (!buffer || labels.length === 0) return null;
  const typed = buffer.toLocaleLowerCase('ro');
  const repeated = [...typed].every((c) => c === typed[0]);
  const prefix = repeated ? typed[0] : typed;
  const start = repeated ? from + 1 : Math.max(from, 0);
  for (let step = 0; step < labels.length; step++) {
    const index = (start + step) % labels.length;
    if (labels[index].toLocaleLowerCase('ro').startsWith(prefix)) return index;
  }
  return null;
}
